import { useState, useCallback, useRef, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from '@/hooks/use-toast';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';
import { checkForExfiltration, EXFIL_REFUSAL_MESSAGE } from '@/lib/aiExfiltrationGuard';
import { classifyPrompt } from '@/lib/moderationPipeline';
import {
  useModerationStrikes,
  recordModerationEvent,
  resetLegacyLocalStorageStrikes,
  MAX_STRIKES as DB_MAX_STRIKES,
} from '@/hooks/useModerationStrikes';
import { SubmitAppealDialog } from '@/components/SubmitAppealDialog';
import { useFeatureFlag } from '@/hooks/useAdminFeatureFlags';
import { type IntentChoice } from '@/components/workflow/IntentSelector';
import { estimatePipelineCost } from '@/lib/aiSkills/costEstimator';
import { planDiagramFromPrompt } from '@/lib/aiSkills/diagramFromPrompt';
import '@/lib/aiSkills/diagramFromCodebase';
import '@/lib/aiSkills/diagramFromSupabaseSchema';
import '@/lib/aiAgents/agents';
import { suggestSkills } from '@/lib/aiSkills/skillRegistry';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { DIAGRAM_PIPELINE } from '@/lib/aiAgents/agentRegistry';
import { useDiagramPipeline } from '@/hooks/useDiagramPipeline';
import { createEdgeLlmCall } from '@/lib/aiAgents/edgeLlmCall';
import { useAiCreditGating } from '@/hooks/useAiCreditGating';
import { loadIntentChoice, saveIntentChoice } from '@/lib/intentChoiceStorage';
import { classifyByKeywords } from '@/lib/intentClassifier';
import {
  type Intent,
  type IntentClassification,
  INTENT_CONFIDENCE_CONFIRM_THRESHOLD,
  DEFAULT_INTENT,
} from '@/lib/intentTypes';
import { buildExtendContext, intentNeedsSnapshot } from '@/lib/aiExtendContext';
import { enrichSnapshotWithEntityDetails } from '@/lib/aiSnapshotEnrichment';
import { checkTokenBudget } from '@/lib/aiTokenBudget';
import { useAiChatHistory } from '@/hooks/useAiChatHistory';
import { useAiChatStream } from '@/hooks/useAiChatStream';
import { useAiImportJson } from '@/hooks/useAiImportJson';
import { ChatSidebar } from '@/components/ai/ChatSidebar';
import { FlowSidebar } from '@/components/ai/FlowSidebar';
import { HistoryUpgradeBanner } from '@/components/ai/HistoryUpgradeBanner';
import { useArtifactMutations } from '@/hooks/useArtifactVersions';
import { usePlan } from '@/hooks/usePlan';
import { aiOps } from '@/lib/aiOpsLog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';


// A-097: legacy strike-/ban-storage flyttad till `@/lib/legacyStrikesStorage`.
// Dialogfilen använder helpers via import (se nedan) — tidigare 5 lokala
// funktioner + 3 konstanter (~40 rader) bor nu single-source.
import {
  LEGACY_MAX_STRIKES,
  isLegacyBanned,
  readLegacyStrikes,
} from '@/lib/legacyStrikesStorage';

// Config-typ + load/save + test-connection bor i useAiEndpointConfig
// (extraherad, #1 i PRE_RELEASE_REVIEW_100).
import { useAiEndpointConfig } from '@/hooks/useAiEndpointConfig';
import { AiEndpointConfigPanel } from '@/components/ai/AiEndpointConfigPanel';
import { AiBannedView } from '@/components/ai/AiBannedView';
import { AiMessageList } from '@/components/ai/AiMessageList';
import { AiChatInputBar } from '@/components/ai/AiChatInputBar';

import { TriggerApprovalDialog } from '@/components/workflow/process/TriggerApprovalDialog';
import {
  dispatchProcessMotorTool,
} from '@/lib/processMotorTools';
import {
  type Msg,
  injectToolResultAsMessage,
} from '@/lib/aiMessageHelpers';

// Comprehensive system prompt that makes the AI an expert on EngiCoro Flow Weaver

interface AiAssistantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (state: WorkflowState | import('@/types/workflow').MultiSheetWorkflowState, overLimit?: boolean) => void;
  /**
   * AA3: Optional — efter import-av-multi-sheet-diagram kan Index switcha
   * till 3D-vyn. Knappen visas i pipeline-preview:n om callback är satt +
   * builtWorkspace har ≥2 sheets.
   */
  onViewIn3D?: () => void;
  /**
   * Optional: route AI-generated workflows through credit-gated staging
   * instead of direct import. When provided, "Import to Canvas"-knappen
   * anropar denna istället — preview-flödet tar över (cost estimation
   * + reserve + staged layer + user approve/discard).
   *
   * I-001: meta-argumentet bär conversationId + intent + promptMessageId
   * så Index.tsx kan skapa en `artifact_version`-rad och emitta
   * `aiOps.artifactCreated` vid Apply.
   */
  onStageAiProposal?: (
    state: WorkflowState,
    meta?: { conversationId?: string; intent?: Intent; promptMessageId?: string },
  ) => void;
  /** Extra system prompt section from the workflow wizard */
  aiHoningPrompt?: string;
  /**
   * Optional: snapshot av nuvarande canvas-state. När intent = 'extend'
   * skickas denna som `previous_version_content` till edge-function så
   * EXTEND-guard-prompten kan bevara befintlig struktur istället för
   * att AI:n skriver över allt.
   *
   * Fas 2: returnera FULL `MultiSheetWorkflowState` (inkl. alla sheets +
   * cross-sheet-connectors) så AI:n får hela kontexten. Single-sheet
   * `WorkflowState` accepteras för bakåtkompat och auto-wrappas.
   */
  getCurrentWorkflowState?: () => WorkflowState | MultiSheetWorkflowState;
  /**
   * Fas 7: aktivt workflow_id. Används för att pre-fetcha entity_details
   * (description_md + attachment-filenames) och injicera dem i extend-
   * mode-snapshoten så AI:n får full per-entity-kontext.
   */
  currentWorkflowId?: string | null;
}


export function AiAssistantDialog({ open, onOpenChange, onImport, onViewIn3D, onStageAiProposal, aiHoningPrompt, getCurrentWorkflowState, currentWorkflowId }: AiAssistantDialogProps) {
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Honing prompt is read at send-time, never at render-time. Tracking
  // it via a ref means `sendMessages` doesn't take it as a dependency,
  // which removes the need for eslint-disable-next-line on the deps.
  const aiHoningPromptRef = useRef(aiHoningPrompt);
  useEffect(() => { aiHoningPromptRef.current = aiHoningPrompt; }, [aiHoningPrompt]);
  // Canvas-state-getter speglas via ref så sendMessages slipper hamna
  // i beroende-listan (den skulle annars invalidera vid varje canvas-ändring).
  const getCurrentWorkflowStateRef = useRef(getCurrentWorkflowState);
  useEffect(() => { getCurrentWorkflowStateRef.current = getCurrentWorkflowState; }, [getCurrentWorkflowState]);
  // Fas 7: aktuellt workflow_id via ref så performSend slipper bero på det.
  const currentWorkflowIdRef = useRef(currentWorkflowId);
  useEffect(() => { currentWorkflowIdRef.current = currentWorkflowId; }, [currentWorkflowId]);

  // Ban / strike enforcement.
  //
  // Moderation v2 (Batch BBB): strikes läses från DB via useModerationStrikes
  // när flag är på. Legacy localStorage-path behålls som fallback om någon
  // slår av flaggan för snabb rollback.
  const moderationV2 = useFeatureFlag<boolean>('moderation_v2_enabled', true);
  const dbStrikes = useModerationStrikes();

  // Nolla legacy localStorage-strikes vid första mount efter rollout.
  useEffect(() => {
    if (moderationV2) resetLegacyLocalStorageStrikes();
  }, [moderationV2]);

  // Lokal UI-state speglar antingen DB (v2) eller localStorage (legacy).
  const [legacyBanned, setLegacyBanned] = useState(isLegacyBanned);
  const [legacyStrikes, setLegacyStrikes] = useState(readLegacyStrikes);
  const banned = moderationV2 ? dbStrikes.isBanned : legacyBanned;
  const strikes = moderationV2 ? dbStrikes.strikeCount : legacyStrikes;
  const maxStrikes = moderationV2 ? DB_MAX_STRIKES : LEGACY_MAX_STRIKES;

  // Moderation-flag-message som visas i senaste chat-entry.
  const [activeFlag, setActiveFlag] = useState<{
    ruleId: string;
    severity: 'advisory' | 'violation';
    humanReadable: string;
    correlationId: string;
    eventId?: string;
  } | null>(null);

  // Appeal-dialog state (öppnas från flag-meddelandet).
  const [appealOpen, setAppealOpen] = useState(false);
  const [appealContext, setAppealContext] = useState<{
    eventId?: string;
    ruleId?: string;
    correlationId?: string;
  }>({});

  const handleAppealClick = useCallback(
    (args: { eventId?: string; ruleId: string; correlationId: string }) => {
      setAppealContext(args);
      setAppealOpen(true);
    },
    [],
  );

  // ── Intent-dropdown + klassificerare (Batch CCC) ───────────────
  // I-006: persist:eras i localStorage så vald intent följer user mellan
  // sessioner. Lazy-init läser vid första render; effect skriver vid ändring.
  const [intentChoice, setIntentChoice] = useState<IntentChoice>(() => loadIntentChoice());
  useEffect(() => { saveIntentChoice(intentChoice); }, [intentChoice]);
  const [pendingConfirm, setPendingConfirm] = useState<{
    prompt: string;
    classification: IntentClassification;
  } | null>(null);
  const [lastAutoClassification, setLastAutoClassification] =
    useState<IntentClassification | null>(null);

  // I-005 B3: pending approval-state för process-motor-write-tools.
  // När AI returnerar tool_call för trigger_workflow_run sätts detta →
  // TriggerApprovalDialog öppnas. User godkänner eller nekar.
  const [pendingToolCall, setPendingToolCall] = useState<{
    id: string;
    name: 'trigger_workflow_run' | 'list_recent_workflow_runs';
    args: Record<string, unknown>;
  } | null>(null);

  // ── Layout-validator (admin-only) ──────────────────────────────
  // A-113: när admin checkar rutan körs `validateLayout` på AI:ns output
  // och brott skickas tillbaka som retry-feedback (max LAYOUT_MAX_RETRIES
  // gånger per send för att undvika oändliga loopar). Reglerna ligger i
  // `documents/v1/SCHEMA_LAYOUT_RULES.md`.
  const [validateLayoutEnabled, setValidateLayoutEnabled] = useState(false);

  // ── Conversation-persistering + chat-historik (Batch CCC/EEE) ──
  // Extraherad till useAiChatHistory (#23): hydrering, sidebar-val,
  // ny-chatt-reset och lazy conversation-skapande.
  const resetTransientChatState = useCallback(() => {
    setActiveFlag(null);
    setPendingConfirm(null);
  }, []);
  const {
    conversationIdRef,
    activeConversationId,
    conversationList,
    loadedMessages,
    appendMessage,
    handleSelectConversation,
    handleNewConversation,
    ensureConversation,
  } = useAiChatHistory({ setMessages, resetTransientState: resetTransientChatState });
  const { tier, limits } = usePlan();
  const { getVersionContent } = useArtifactMutations();
  // AB1: admin-flag för skill-filtret. Icke-admin får inte se
  // diagramFromCodebase / diagramFromSupabaseSchema i quick-action
  // eller via prompt-trigger — skillen returnerar ändå error-plan
  // defense-in-depth om suggestSkills-filtret skulle kringgås.
  const { isAdmin } = useAdminAuth();

  // X2/X3: agent-pipeline för diagram-authoring. Live-stepper uppdateras
  // från useDiagramPipeline-state. LlmCall lazy-init:as en gång per mount
  // så AbortController + auth-token-factory är stabila.
  const diagramLlmCallRef = useRef(createEdgeLlmCall());
  const { preflightAndReserve, commitReservation, releaseReservation } = useAiCreditGating();
  // Y1: credit-gating-deps memo:as inte — hooken använder inputRef så nya
  // objekt per render är OK. estimateCredits räknas lazy från input-prompten
  // i Y1-blocket nedan (när start() kallas med det aktuella promptvärdet).
  // Live cost-estimate baserat på aktuellt input-värde. Deterministisk,
  // så räkningen per render är billig. Används av credit-preflight innan
  // pipeline-start. 0 när input är tomt → hooken hoppar över gating.
  const diagramEstimatedCredits = (() => {
    if (!input.trim() || suggestSkills(input, { isAdmin }).length === 0) return 0;
    const plan = planDiagramFromPrompt({ userPrompt: input });
    return estimatePipelineCost(plan, DIAGRAM_PIPELINE.map((s) => s.agentId)).total;
  })();
  const diagramPipeline = useDiagramPipeline({
    llmCall: diagramLlmCallRef.current,
    estimatedCredits: diagramEstimatedCredits,
    // AA2e: admin-gated loaders. Non-admin → loaders utelämnas →
    // buildWorkspace-step fall:er tillbaka till diagramFromPrompt.
    isAdmin,
    loadSchema: isAdmin
      ? async () => {
          const { loadSupabaseSchema } = await import('@/lib/aiSkills/supabaseSchemaLoader');
          return loadSupabaseSchema();
        }
      : undefined,
    // NOTE (standalone-split carve): the self-source codebase loader was
    // removed. It used `import.meta.glob('/src/**/*', { as: 'raw' })`, which
    // inlined the app's entire source tree (~10 MB) into the bundle just to
    // let an admin "diagram this app's own code". With the loader absent the
    // 'codebase' pipeline branch is skipped and buildWorkspace falls back to
    // diagramFromPrompt (see useDiagramPipeline). loadCodebaseFiles is
    // optional, so omitting it is type-safe.
    creditGating: {
      preflightAndReserve: async ({ correlationId, estimatedAiCredits }) => {
        const res = await preflightAndReserve({ correlationId, estimatedAiCredits });
        return { ok: res.ok, reason: res.reason };
      },
      commitReservation: async ({ correlationId, actualCredits }) => {
        const res = await commitReservation({ correlationId, actualCredits });
        return { ok: res.ok, refunded: res.refunded };
      },
      releaseReservation: async ({ correlationId }) => {
        const res = await releaseReservation({ correlationId });
        return { ok: res.ok, released: res.released };
      },
    },
  });

  // Load sparad artifact-version till canvas via stage-layer (cost=0).
  const handleLoadVersion = useCallback(
    async (versionId: string, _artifactId: string) => {
      const content = await getVersionContent(versionId);
      if (!content) {
        toast({ title: 'Kunde inte ladda versionen', variant: 'destructive' });
        return;
      }
      if (onStageAiProposal) {
        // Passar stagedState med metadata så Index.tsx kan rendera som load (cost=0)
        const stateWithLoadMarker = { ...content, name: content.name || 'Laddad version' };
        onStageAiProposal(stateWithLoadMarker);
        toast({ title: 'Version laddad', description: 'Apply/Discard på canvas' });
      } else {
        onImport(content);
        toast({ title: 'Version importerad', description: 'Canvas uppdaterad' });
      }
    },
    [getVersionContent, onStageAiProposal, onImport],
  );

  // I-104: "Fortsätt bygga" — laddar version + sätter intent=extend +
  // focus:ar chat-input så user kan skriva nästa iteration direkt.
  const handleContinueBuilding = useCallback(
    async (versionId: string, artifactId: string) => {
      await handleLoadVersion(versionId, artifactId);
      setIntentChoice('extend');
      // RAF för att inputen hinner re-render:a innan vi focus:ar.
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLTextAreaElement>('[data-testid="ai-chat-input"]')
          ?? document.querySelector<HTMLTextAreaElement>('textarea[placeholder*="Beskriv"]');
        el?.focus();
      });
      toast({
        title: 'Fortsätt bygga',
        description: 'Versionen är laddad + intent=extend. Skriv nästa steg.',
      });
    },
    [handleLoadVersion],
  );

  // A-017: createVersion-destructure + void-stub togs bort — version-
  // skapande sker i Index.tsx:handleApplyAiProposal (via useArtifactMutations
  // där). Denna dialog konsumerar bara getVersionContent (läs-väg, för att
  // ladda en sparad version tillbaka till canvas).



  // Endpoint-config (load/persist, draft, test-connection, chat-URL).
  const {
    config,
    configDraft,
    setConfigDraft,
    testStatus,
    isConfigured,
    getChatUrl,
    persistConfig,
    handleSaveConfig,
    handleTestConnection,
  } = useAiEndpointConfig({ onSaved: () => setShowConfig(false) });

  // Show config on open if not configured
  useEffect(() => {
    if (open && !isConfigured) {
      setShowConfig(true);
    }
  }, [open, isConfigured]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Stream-loopen (SSE, moderation, tool-calls, layout-retry, model-
  // fallback) — extraherad till useAiChatStream (#1 slutskivan).
  const {
    sendMessages,
    chatAbortRef,
    layoutRetryCountRef,
  } = useAiChatStream({
    setIsLoading,
    setMessages,
    config,
    getChatUrl,
    persistConfig,
    moderationV2,
    dbStrikes,
    appendMessage,
    conversationIdRef,
    validateLayoutEnabled,
    aiHoningPromptRef,
    setActiveFlag,
    setPendingToolCall,
    setLegacyStrikes,
    setLegacyBanned,
  });

  // performSendRef ger option-click samma persist-path som manuell
  // input (conversation + appendMessage).
  const performSendRef = useRef<
    (t: string, c: IntentClassification, override: boolean) => Promise<void>
  >(async () => {});

  const handleOptionClick = useCallback((optionText: string) => {
    if (banned || isLoading) return;
    // Option-click = user-svar på AI:ns följdfråga. Route genom performSend
    // så den blir persisterad (ensureConversation + appendMessage) och
    // syns när user laddar tillbaka chatten senare.
    const kw = classifyByKeywords(optionText);
    const classification: IntentClassification = kw ?? {
      intent: DEFAULT_INTENT,
      confidence: 0,
      source: 'keyword',
      reasoning: 'option-click fallback',
    };
    void performSendRef.current(optionText, classification, false);
  }, [banned, isLoading]);


  /**
   * Kärn-proceduren efter intent är känd och moderation passerat —
   * persist:erar user-prompten (med intent) och skickar till AI.
   */
  const performSend = useCallback(
    async (
      trimmed: string,
      classification: IntentClassification,
      isOverride: boolean,
      moderationEventId?: string,
    ) => {
      const convId = await ensureConversation();
      if (convId) {
        await appendMessage({
          conversationId: convId,
          role: 'user',
          content: trimmed,
          intent: classification.intent,
          intentOverride: isOverride ? classification.intent : undefined,
          intentConfidence: classification.confidence,
          intentSource: classification.source,
          moderationEventId,
        });
      }

      setActiveFlag(null);
      setPendingConfirm(null);
      if (classification.source !== 'manual') {
        setLastAutoClassification(classification);
      }

      // Emit ai_ops event för intent-klassificering (Batch FFF).
      void aiOps.intentClassified({
        conversationId: convId ?? undefined,
        intent: classification.intent,
        confidence: classification.confidence,
        source: classification.source,
        reasoning: classification.reasoning,
      });
      const userMsg: Msg = { role: 'user', content: trimmed };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');

      // A-113: nollställ layout-retry-räknaren vid varje user-initierad send
      // så retry-cap:en gäller per fråga, inte per session.
      layoutRetryCountRef.current = 0;

      // Bug-B fix: tidigare skickades snapshot ENBART vid intent='extend'.
      // Refactor/modify_in_place/explain behöver också canvas-context för
      // att AI:n ska kunna analysera/ändra befintligt flöde — annars
      // hallucinerar den eller refuserar. intentNeedsSnapshot returnerar
      // true för alla canvas-relevanta intents.
      // Fas 7: vid samma intent-set hämtar vi entity_details (description_md
      // + attachment-filenames) och injicerar i snapshoten så AI:n får
      // per-entity-kontext, inte bara block-namn + position. Fail-soft.
      const snapshot = getCurrentWorkflowStateRef.current?.();
      const enriched = intentNeedsSnapshot(classification.intent)
        ? await enrichSnapshotWithEntityDetails(snapshot, currentWorkflowIdRef.current)
        : snapshot;

      // Fas 4: token-budget pre-flight. Vid 'critical' visar vi en toast så
      // user kan välja att aborta innan AI:n silent-truncerar output:en.
      const budget = checkTokenBudget({
        snapshot: enriched,
        conversation: [...messages, userMsg],
        honingPromptChars: aiHoningPromptRef.current?.length ?? 0,
        model: config.model || 'google/gemini-2.5-flash',
      });
      if (budget.tier === 'critical') {
        toast({
          title: 'Token-budget överskrids',
          description: `Estimerat ${budget.estimatedPromptTokens.toLocaleString()} tokens — > 85% av kontextfönstret. Risken är hög att AI:n trunkerar output. Dela upp begäran eller byt till en modell med större kontext.`,
          variant: 'destructive',
        });
      } else if (budget.tier === 'warn') {
        toast({
          title: 'Stor prompt',
          description: `Estimerat ${budget.estimatedPromptTokens.toLocaleString()} tokens (${Math.round(budget.utilization * 100)}% av context-window). Output kan bli försämrat.`,
        });
      }

      const extendContext = buildExtendContext(classification.intent, enriched);
      sendMessages([...messages, userMsg], extendContext);
    },
    // config.model läses synkront via budget-checken; vi vill INTE
    // re-bilda performSend bara för att model-konfigen byts (det skulle
    // invalidera ref:en i performSendRef + skapa stale closure-bug).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appendMessage, ensureConversation, messages, sendMessages],
  );

  useEffect(() => { performSendRef.current = performSend; }, [performSend]);

  const handleSend = useCallback(async () => {
    if (banned) return;
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    // ── Moderation-check först (Batch BBB) ─────────────────────────
    if (moderationV2) {
      const verdict = classifyPrompt(trimmed);

      if (verdict.flagged && verdict.verdict) {
        const rec = await recordModerationEvent({
          ruleId: verdict.verdict.ruleId,
          ruleVersion: verdict.verdict.ruleVersion,
          severity: verdict.verdict.severity,
          humanReadable: verdict.verdict.humanReadable,
          matchedPattern: verdict.verdict.matchedPattern,
          correlationId: verdict.verdict.correlationId,
        });

        // Persistera user-prompt + moderation-event i conversation
        const convId = await ensureConversation();
        if (convId && rec.eventId) {
          await appendMessage({
            conversationId: convId,
            role: 'user',
            content: trimmed,
            moderationEventId: rec.eventId,
          });
        }

        setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
        setInput('');
        setActiveFlag({
          ruleId: verdict.verdict.ruleId,
          severity: verdict.verdict.severity,
          humanReadable: verdict.verdict.humanReadable,
          correlationId: verdict.verdict.correlationId,
          eventId: rec.eventId,
        });
        dbStrikes.refresh();

        // Emit ai_ops moderation-flag event (Batch FFF).
        if (rec.eventId) {
          void aiOps.moderationFlag({
            conversationId: convId ?? undefined,
            moderationEventId: rec.eventId,
            ruleId: verdict.verdict.ruleId,
            severity: verdict.verdict.severity,
          });
        }
        return;
      }
      // Ej flaggad → fortsätt till intent-klassificering.
    } else {
      const exfil = checkForExfiltration(trimmed);
      if (exfil.blocked) {
        setMessages((prev) => [
          ...prev,
          { role: 'user', content: trimmed },
          { role: 'assistant', content: EXFIL_REFUSAL_MESSAGE },
        ]);
        setInput('');
        return;
      }
    }

    // ── Intent-klassificering (Batch CCC) ─────────────────────────
    if (intentChoice !== 'auto') {
      // Manuell override — ingen klassificering, max confidence.
      await performSend(
        trimmed,
        {
          intent: intentChoice,
          confidence: 1.0,
          source: 'manual',
        },
        true,
      );
      return;
    }

    // Auto: kör keyword-klassificerare. LLM-fallback är frivillig
    // (körs bara om keyword returnerar null). Vi håller det här enkelt
    // för att inte dubbla nätverkskall i varje send.
    const kwClassification = classifyByKeywords(trimmed) ?? {
      intent: DEFAULT_INTENT,
      confidence: 0.4,
      source: 'keyword' as const,
      reasoning: 'no-keyword-match, fallback to default',
    };

    // Låg confidence → be user bekräfta innan AI-call.
    if (kwClassification.confidence < INTENT_CONFIDENCE_CONFIRM_THRESHOLD) {
      setPendingConfirm({ prompt: trimmed, classification: kwClassification });
      return;
    }

    await performSend(trimmed, kwClassification, false);
  }, [
    input, isLoading, banned, moderationV2, intentChoice,
    dbStrikes, performSend, appendMessage, ensureConversation,
  ]);

  // Handlers för confirmation-banner
  const handleConfirmIntent = useCallback(
    async (intent: Intent) => {
      if (!pendingConfirm) return;
      await performSend(
        pendingConfirm.prompt,
        { ...pendingConfirm.classification, intent },
        // Om user-vald intent skiljer från original → manual override
        intent !== pendingConfirm.classification.intent,
      );
    },
    [pendingConfirm, performSend],
  );

  const handleCancelIntent = useCallback(() => {
    setPendingConfirm(null);
  }, []);

  // #25: Stop-handler för pågående AI-stream. Visar "Stopping…" tills
  // stream-loop:ens finally återställer isLoading.
  const [isStopping, setIsStopping] = useState(false);
  const handleStop = useCallback(() => {
    if (!isLoading) return;
    setIsStopping(true);
    chatAbortRef.current?.abort();
  }, [isLoading, chatAbortRef]);
  useEffect(() => {
    if (!isLoading && isStopping) setIsStopping(false);
  }, [isLoading, isStopping]);

  const handleImportJson = useAiImportJson({
    onImport,
    onStageAiProposal,
    onOpenChange,
    conversationIdRef,
    lastAutoClassification,
  });

  const configView = (
    <AiEndpointConfigPanel
      configDraft={configDraft}
      setConfigDraft={setConfigDraft}
      testStatus={testStatus}
      isConfigured={isConfigured}
      onTestConnection={handleTestConnection}
      onSaveConfig={handleSaveConfig}
      onBackToChat={() => setShowConfig(false)}
    />
  );

  const bannedView = (
    <AiBannedView
      minHeight={isMobile ? '60vh' : '500px'}
      onAppeal={() => setAppealOpen(true)}
    />
  );

  const chatView = (
    <div className="flex flex-col h-full" style={{ minHeight: isMobile ? '60vh' : '500px' }}>
      {/* Strike warning banner */}
      {strikes > 0 && strikes < maxStrikes && !banned && (
        <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2">
          <span className="font-semibold">⚠️ Warning:</span>
          <span>Policy violation {strikes}/{maxStrikes} — further misuse will result in a permanent ban.</span>
        </div>
      )}

      <ScrollArea className="flex-1 pr-2" ref={scrollRef as React.Ref<HTMLDivElement>}>
        <AiMessageList
          messages={messages}
          isLoading={isLoading}
          activeConversationId={activeConversationId}
          loadedMessages={loadedMessages}
          configEndpoint={config.endpoint}
          configModel={config.model}
          strikes={strikes}
          maxStrikes={maxStrikes}
          activeFlag={activeFlag}
          pendingConfirm={pendingConfirm}
          onOptionClick={handleOptionClick}
          onImportJson={handleImportJson}
          onAppealClick={handleAppealClick}
          onConfirmIntent={handleConfirmIntent}
          onCancelIntent={handleCancelIntent}
          onPickExample={(prompt) => {
            setInput(prompt);
            inputRef.current?.focus();
          }}
        />
      </ScrollArea>

      <AiChatInputBar
        input={input}
        setInput={setInput}
        inputRef={inputRef}
        isLoading={isLoading}
        banned={banned}
        isAdmin={isAdmin}
        intentChoice={intentChoice}
        setIntentChoice={setIntentChoice}
        lastAutoClassification={lastAutoClassification}
        validateLayoutEnabled={validateLayoutEnabled}
        setValidateLayoutEnabled={setValidateLayoutEnabled}
        diagramPipeline={diagramPipeline}
        onImport={onImport}
        onViewIn3D={onViewIn3D}
        onOpenChange={onOpenChange}
        onOpenSettings={() => { setConfigDraft(config); setShowConfig(true); }}
        onSend={handleSend}
        onStop={handleStop}
        isStopping={isStopping}
        modelName={config.model || 'google/gemini-2.5-flash'}
        honingPromptChars={aiHoningPrompt?.length ?? 0}
        getSnapshot={getCurrentWorkflowState}
      />
    </div>
  );

  const content = banned ? bannedView : showConfig ? configView : chatView;

  // Globala dialoger renderas top-level så de mountas oavsett om vi visar
  // chatView, bannedView eller configView. Tidigare bug: SubmitAppealDialog
  // låg inuti chatView → klick på "Överklaga banet"-knappen i bannedView
  // gjorde inget eftersom dialogen aldrig mountades vid banned===true.
  const globalDialogs = (
    <>
      <SubmitAppealDialog
        open={appealOpen}
        onOpenChange={setAppealOpen}
        moderationEventId={appealContext.eventId}
        moderationRuleId={appealContext.ruleId}
        moderationCorrelationId={appealContext.correlationId}
      />
      <TriggerApprovalDialog
        open={pendingToolCall !== null}
        onOpenChange={(o) => { if (!o) setPendingToolCall(null); }}
        call={pendingToolCall
          ? { name: pendingToolCall.name, args: pendingToolCall.args }
          : null}
        onApprove={async (call) => {
          const result = await dispatchProcessMotorTool(call, { approved: true });
          injectToolResultAsMessage(setMessages, call.name, result);
          setPendingToolCall(null);
          return result;
        }}
        onDeny={(call) => {
          injectToolResultAsMessage(setMessages, call.name, {
            ok: false, error: 'user-denied',
          });
          setPendingToolCall(null);
        }}
      />
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[85vh] p-0 flex flex-col">
          <SheetTitle className="px-4 pt-4 pb-2 text-base font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" /> AI Assistant
          </SheetTitle>
          {content}
          {globalDialogs}
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: 3-kolumns layout (chat-sidebar | chat | flow-sidebar) om
  // chat-view visas. Configview / bannedview behåller single-column.
  const showSidebars = !banned && !showConfig;
  const activeCount = (conversationList ?? []).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`${showSidebars ? 'max-w-6xl' : 'max-w-2xl'} max-h-[85vh] p-0 flex flex-col overflow-hidden`}
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> AI Assistant
          </DialogTitle>
        </DialogHeader>
        {showSidebars ? (
          <div className="flex-1 grid grid-cols-[220px_1fr_260px] min-h-0 overflow-hidden">
            <div className="flex flex-col min-h-0">
              <HistoryUpgradeBanner
                currentCount={activeCount}
                limit={limits.maxConversations}
                currentTier={tier}
                requiredTier={
                  tier === 'free' ? 'advanced'
                    : tier === 'advanced' ? 'pro'
                    : tier === 'pro' ? 'max'
                    : tier === 'max' ? 'enterprise'
                    : undefined
                }
              />
              <ChatSidebar
                scope="workflow_ai"
                activeConversationId={activeConversationId}
                onSelectConversation={handleSelectConversation}
                onNewConversation={handleNewConversation}
              />
            </div>
            <div className="min-w-0 overflow-hidden">{content}</div>
            <FlowSidebar
              conversationId={activeConversationId}
              onLoadVersion={handleLoadVersion}
              onContinueBuilding={handleContinueBuilding}
            />
          </div>
        ) : (
          content
        )}
        {globalDialogs}
      </DialogContent>
    </Dialog>
  );
}
